import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import type { TicketPriority, TicketStatus } from "../../Models/helpDesk";
import { useSingleTicket } from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import AttachmentList from "./AttachmentList";
import TicketConversation from "./TicketConversation";

type Props = {
  ticketId?: number;
  onClose: () => void;
};

const TicketDetailsModal: React.FC<Props> = ({ ticketId, onClose }) => {
  const { t } = useTranslation("helpDesk");
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useSingleTicket(ticketId);


  const statusLabels: Record<TicketStatus, string> = {
    0: t("status.submitted"),
    1: t("status.inProgress"),
    2: t("status.closed"),
  };

  const priorityLabels: Record<TicketPriority, string> = {
    0: t("priority.low"),
    1: t("priority.normal"),
    2: t("priority.high"),
  };

  const statusStyles = [
    { bg: color.orange50, text: color.accent_dark },
    { bg: color.blue50, text: color.secondary },
    { bg: color.green50, text: color.green700 },
  ];

  const priorityStyles = [
    { bg: color.gray50, text: color.gray500 },
    { bg: color.blue50, text: color.secondary },
    { bg: color.red50, text: color.red700 },
  ];

  const statusStyle = ticket
    ? (statusStyles[ticket.status] ?? statusStyles[0])
    : statusStyles[0];

  const priorityStyle = ticket
    ? (priorityStyles[ticket.priority] ?? priorityStyles[1])
    : priorityStyles[1];

  const close = () => onClose();

  const labelSx = {
    color: `${color.gray500} !important`,
    fontSize: 12,
    fontWeight: 600,
  };

  const valueSx = {
    color: `${color.primary_dark} !important`,
    fontSize: 13,
    fontWeight: 700,
    overflowWrap: "anywhere",
  };

  const infoRow = (label: string, value: React.ReactNode) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "95px minmax(0, 1fr)",
          sm: "120px minmax(0, 1fr)",
        },
        gap: 1.5,
        alignItems: "center",
        py: 1.5,
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      <Box sx={{ minWidth: 0 }}>{value}</Box>
    </Box>
  );

  return (
    <Dialog
      open={ticketId !== undefined}
      onClose={close}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1320px, 97vw)",
          maxWidth: "none",
          height: {
            xs: "96dvh",
            md: "min(880px, 94dvh)",
          },
          maxHeight: "96dvh",
          m: 1,
          borderRadius: "18px",
          bgcolor: color.white,
          backgroundImage: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: `1px solid ${color.gray200}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
          bgcolor: color.white,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: color.blue50,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <AssignmentOutlinedIcon sx={{ color: color.secondary }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: `${color.secondary} !important`,
                fontSize: 11,
                fontWeight: 800,
                mb: 0.3,
              }}
            >
              {ticket ? `#${ticket.id}` : t("details.title")}
            </Typography>

            <Typography
              sx={{
                color: `${color.primary_dark} !important`,
                fontSize: { xs: 16, md: 21 },
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ticket?.title ?? t("details.title")}
            </Typography>
          </Box>
        </Stack>

        <IconButton
          onClick={close}
          aria-label={t("actions.closeDialog")}
          sx={{
            bgcolor: color.gray50,
            borderRadius: "10px",
            "&:hover": {
              bgcolor: color.blue50,
            },
          }}
        >
          <CloseIcon sx={{ color: color.gray500 }} />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress sx={{ color: color.accent }} />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button onClick={() => refetch()}>{t("actions.retry")}</Button>
            }
          >
            {t("details.loadError")}
          </Alert>
        </Box>
      ) : !ticket ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t("tickets.notFound")}</Alert>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
            },
            flex: 1,
            minHeight: 0,
            overflowY: {
              xs: "auto",
              md: "hidden",
            },
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              minWidth: 0,
              overflowY: {
                xs: "visible",
                md: "auto",
              },
              bgcolor: color.white,
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <DescriptionOutlinedIcon
                    sx={{
                      color: color.secondary,
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("details.description")}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    bgcolor: color.gray50,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "12px",
                    p: 2.5,
                    minHeight: 130,
                  }}
                >
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 13,
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {ticket.description || t("tickets.noDescription")}
                  </Typography>
                  <AttachmentList attachments={ticket.attachments ?? []} />
                </Box>
              </Box>

              <Divider sx={{ borderColor: color.gray200 }} />

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <LocalOfferOutlinedIcon
                    sx={{
                      color: color.secondary,
                      fontSize: 20,
                    }}
                  />

                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("createDialog.propertiesSection")}
                  </Typography>
                </Stack>

                {infoRow(
                  t("details.status"),
                  <Chip
                    size="small"
                    label={statusLabels[ticket.status] ?? t("status.unknown")}
                    sx={{
                      bgcolor: `${statusStyle.bg} !important`,
                      color: `${statusStyle.text} !important`,
                      fontSize: 11,
                      fontWeight: 800,
                      borderRadius: "7px",
                    }}
                  />,
                )}

                <Divider sx={{ borderColor: color.gray200 }} />

                {infoRow(
                  t("details.priority"),
                  <Chip
                    size="small"
                    label={
                      priorityLabels[ticket.priority] ?? t("priority.normal")
                    }
                    sx={{
                      bgcolor: `${priorityStyle.bg} !important`,
                      color: `${priorityStyle.text} !important`,
                      fontSize: 11,
                      fontWeight: 800,
                      borderRadius: "7px",
                    }}
                  />,
                )}

                <Divider sx={{ borderColor: color.gray200 }} />

                {infoRow(
                  t("details.category"),
                  <Typography sx={valueSx}>
                    {ticket.category || t("tickets.notSpecified")}
                  </Typography>,
                )}

                <Divider sx={{ borderColor: color.gray200 }} />

                {infoRow(
                  t("details.project"),
                  <Typography sx={valueSx}>
                    {ticket.workspace?.name ?? t("tickets.general")}
                  </Typography>,
                )}

                <Divider sx={{ borderColor: color.gray200 }} />

                {infoRow(
                  t("createDialog.tags"),
                  ticket.tags?.length ? (
                    <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.6}>
                      {ticket.tags.map((tag, index) => (
                        <Chip
                          key={`${tag}-${index}`}
                          label={`#${tag}`}
                          size="small"
                          sx={{
                            bgcolor: `${color.orange50} !important`,
                            color: `${color.accent_dark} !important`,
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: "7px",
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={valueSx}>/</Typography>
                  ),
                )}
              </Box>

              {ticket.resolution && (
                <>
                  <Divider sx={{ borderColor: color.gray200 }} />

                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={1.5}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          color: color.green700,
                          fontSize: 20,
                        }}
                      />

                      <Typography
                        sx={{
                          color: `${color.primary_dark} !important`,
                          fontSize: 15,
                          fontWeight: 800,
                        }}
                      >
                        {t("details.resolution")}
                      </Typography>
                    </Stack>

                    <Alert
                      severity="success"
                      sx={{
                        borderRadius: "10px",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {ticket.resolution}
                    </Alert>
                  </Box>
                </>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: {
                xs: 460,
                md: 0,
              },
              overflow: "hidden",
              bgcolor: color.gray50,
              borderLeft: {
                xs: "none",
                md: `1px solid ${color.gray200}`,
              },
              borderTop: {
                xs: `1px solid ${color.gray200}`,
                md: "none",
              },
            }}
          >
            <TicketConversation key={ticket.id} ticket={ticket} onSent={refetch} />
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default TicketDetailsModal;
